/**
 * Locale routing configuration.
 *
 * We use a "non-routing" strategy: locale is stored in NEXT_LOCALE cookie,
 * NOT in URL segments. This file exports shared constants for:
 * - middleware locale detection
 * - LanguageSwitcher component
 * - any server-side locale resolution
 *
 * 25 languages across 5 tiers covering all major global markets.
 */

export const SUPPORTED_LOCALES = [
  // Tier 1 — Latin script, Western European (9)
  "en-GB", "en-US", "es", "fr", "de", "it", "nl", "pt-BR", "sv",
  // Tier 2 — Eastern European & Turkic (3)
  "pl", "tr", "ru",
  // Tier 3 — Asian (CJK + SEA) (6)
  "zh-CN", "zh-TW", "ja", "ko", "th", "id",
  // Tier 4 — South Asian (Indian languages) (6)
  "hi", "ta", "te", "bn", "mr", "kn",
  // Tier 5 — RTL (1)
  "ar",
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = "en-GB";

/** Locales that require right-to-left layout */
export const RTL_LOCALES: readonly string[] = ["ar"] as const;

/** Human-readable names for the language switcher */
export const LOCALE_LABELS: Record<SupportedLocale, { name: string; flag: string }> = {
  // Tier 1 — Western European
  "en-GB": { name: "English (UK)", flag: "\u{1F1EC}\u{1F1E7}" },
  "en-US": { name: "English (US)", flag: "\u{1F1FA}\u{1F1F8}" },
  "es":    { name: "Espa\u00f1ol", flag: "\u{1F1EA}\u{1F1F8}" },
  "fr":    { name: "Fran\u00e7ais", flag: "\u{1F1EB}\u{1F1F7}" },
  "de":    { name: "Deutsch", flag: "\u{1F1E9}\u{1F1EA}" },
  "it":    { name: "Italiano", flag: "\u{1F1EE}\u{1F1F9}" },
  "nl":    { name: "Nederlands", flag: "\u{1F1F3}\u{1F1F1}" },
  "pt-BR": { name: "Portugu\u00eas (Brasil)", flag: "\u{1F1E7}\u{1F1F7}" },
  "sv":    { name: "Svenska", flag: "\u{1F1F8}\u{1F1EA}" },
  // Tier 2 — Eastern European & Turkic
  "pl":    { name: "Polski", flag: "\u{1F1F5}\u{1F1F1}" },
  "tr":    { name: "T\u00fcrk\u00e7e", flag: "\u{1F1F9}\u{1F1F7}" },
  "ru":    { name: "\u0420\u0443\u0441\u0441\u043a\u0438\u0439", flag: "\u{1F1F7}\u{1F1FA}" },
  // Tier 3 — Asian (CJK + SEA)
  "zh-CN": { name: "\u7b80\u4f53\u4e2d\u6587", flag: "\u{1F1E8}\u{1F1F3}" },
  "zh-TW": { name: "\u7e41\u9ad4\u4e2d\u6587", flag: "\u{1F1F9}\u{1F1FC}" },
  "ja":    { name: "\u65e5\u672c\u8a9e", flag: "\u{1F1EF}\u{1F1F5}" },
  "ko":    { name: "\ud55c\uad6d\uc5b4", flag: "\u{1F1F0}\u{1F1F7}" },
  "th":    { name: "\u0e44\u0e17\u0e22", flag: "\u{1F1F9}\u{1F1ED}" },
  "id":    { name: "Bahasa Indonesia", flag: "\u{1F1EE}\u{1F1E9}" },
  // Tier 4 — South Asian (Indian languages)
  "hi":    { name: "\u0939\u093f\u0928\u094d\u0926\u0940", flag: "\u{1F1EE}\u{1F1F3}" },
  "ta":    { name: "\u0ba4\u0bae\u0bbf\u0bb4\u0bcd", flag: "\u{1F1EE}\u{1F1F3}" },
  "te":    { name: "\u0c24\u0c46\u0c32\u0c41\u0c17\u0c41", flag: "\u{1F1EE}\u{1F1F3}" },
  "bn":    { name: "\u09ac\u09be\u0982\u09b2\u09be", flag: "\u{1F1EE}\u{1F1F3}" },
  "mr":    { name: "\u092e\u0930\u093e\u0920\u0940", flag: "\u{1F1EE}\u{1F1F3}" },
  "kn":    { name: "\u0c95\u0ca8\u0ccd\u0ca8\u0ca1", flag: "\u{1F1EE}\u{1F1F3}" },
  // Tier 5 — RTL
  "ar":    { name: "\u0627\u0644\u0639\u0631\u0628\u064a\u0629", flag: "\u{1F1F8}\u{1F1E6}" },
};

/** Map locale to legal region for privacy/terms content */
export function getRegionFromLocale(locale: SupportedLocale): "eu" | "us" | "international" {
  switch (locale) {
    case "en-GB":
    case "fr":
    case "de":
    case "it":
    case "nl":
    case "es":
    case "sv":
    case "pl":
      return "eu";
    case "en-US":
      return "us";
    default:
      return "international";
  }
}

/**
 * Parse Accept-Language header and return best matching locale.
 * Falls back to DEFAULT_LOCALE if no match.
 */
export function negotiateLocale(acceptLanguage: string | null): SupportedLocale {
  if (!acceptLanguage) return DEFAULT_LOCALE;

  // Parse "en-GB,en;q=0.9,fr;q=0.8" into sorted preference list
  const preferences = acceptLanguage
    .split(",")
    .map((part) => {
      const [lang, qStr] = part.trim().split(";q=");
      return { lang: lang.trim(), q: qStr ? parseFloat(qStr) : 1.0 };
    })
    .sort((a, b) => b.q - a.q);

  for (const { lang } of preferences) {
    // Exact match (e.g., "en-GB")
    if (SUPPORTED_LOCALES.includes(lang as SupportedLocale)) {
      return lang as SupportedLocale;
    }
    // Base language match (e.g., "en" → "en-GB", "fr" → "fr")
    const baseMatch = SUPPORTED_LOCALES.find(
      (l) => l.startsWith(lang) || l.split("-")[0] === lang
    );
    if (baseMatch) return baseMatch;
  }

  return DEFAULT_LOCALE;
}
